import React, { useState, useEffect } from 'react';
import { ProfileCatalogProduct } from '@/types';
import { searchProfileCatalogProducts } from '@/services/profile-catalog-products';
import { useDebounce } from '@/utils/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons';
import theme from './ProductSearch.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Input } from '@/components/generic-controls';
import { classnames } from '@/utils/html';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { LoadingOverlay } from '@ygtechnologies/bygr-frontend-component-library';
import { ProductCard, ProductCardProps } from './ProductCard';

type ProductSearchProps = Pick<ProductCardProps, 'onCheckChange'> & {
    profileId: number;
    checkedProductIds?: number[];
    resultsClassName?: string;
    inputClassName?: string;
    itemCategoryId?: number | null;
};

const ProductSearch: React.FC<ProductSearchProps> = ({
    profileId,
    onCheckChange,
    checkedProductIds,
    itemCategoryId,
    resultsClassName,
    inputClassName,
}) => {
    const [products, setProducts] = useState<ProfileCatalogProduct[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);

    const searchTextDebounced = useDebounce(searchText, 250);
    useEffect(() => {
        setLoading(true);
    }, [searchTextDebounced]);

    useEffect(() => {
        async function loadData() {
            const result = await searchProfileCatalogProducts({
                ownerProfileId: profileId,
                searchText: searchTextDebounced,
                itemCategoryIds: itemCategoryId ? [itemCategoryId] : [],
            });

            setProducts(result);
            setLoading(false);
        }

        setLoading(true);
        loadData();
    }, [searchTextDebounced, itemCategoryId, profileId]);

    return (
        <div className={theme.container}>
            <div className={theme.inputContainer}>
                <Input
                    type="text"
                    className={classnames('form-control', inputClassName)}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={t`Search...`}
                />
                {loading && <FontAwesomeIcon icon={faSpinnerThird} spin className={theme.spinner} />}
            </div>

            {itemCategoryId && (
                <small>
                    Showing only <ItemCategoryDisplay value={itemCategoryId} type="self" lowercase />
                </small>
            )}
            <div className={classnames(theme.results, resultsClassName)}>
                <LoadingOverlay visible={loading && products.length > 0}>
                    {products.length === 0 && !loading && <Trans>No results</Trans>}
                    {products.map((p) => (
                        <ProductCard
                            key={p.id}
                            catalog="profile"
                            product={p}
                            onCheckChange={onCheckChange}
                            checked={checkedProductIds ? checkedProductIds.includes(p.id) : false}
                        />
                    ))}
                </LoadingOverlay>
            </div>
        </div>
    );
};

export default ProductSearch;
